import { useCallback, useEffect } from 'react'
import { graphql, PageProps } from 'gatsby'
import tw from 'twin.macro'
import PageWrapper from '@/components/page-wrapper'
import RichText from '@/components/ui/rich-text'
import SectionCta from '@/components/section/cta'
import SectionForm from '@/components/section/form'
import SectionPrebuilt from '@/components/section/prebuilt'
import SetOfCards from '@/components/section/set-of-cards'
import SectionFaqs from '@/components/section/faqs'
import SetOfImages from '@/components/section/set-of-images'
import { SetOfMasonaryCards } from '@/components/section/set-of-masonary-cards'
import SectionSetOfPricing from '@/components/section/set-of-pricing'
import SectionTabSection from '@/components/section/tab-section'
import Image from '@/components/ui/image'
import colors from '@/constants/colors'
import Quote from '@/components/ui/quote'
import SliderSection from '@/components/section/slider-section'
import BarGraph from '@/components/bar-graph'
import Lottie from '@/components/lottie'
import ComparisonTable from '@/components/comparison-table'
import Table from '@/components/table'
import EventSchedule from '@/components/event-schedule/event-schedule'
import { useNinetailed } from '@ninetailed/experience.js-gatsby'
import { getInspectorProps } from '@/utils/live-preview'
import { getBrowserName, getOs } from '@/utils/user-info'
import { isPrint } from '@/utils/print'
import PrintWrapper, { printCoverImage } from '@/components/print-wrapper'

import { useLocation } from '@reach/router'
import { buttonStyles } from '@/components/ui/cta-link'
import DownloadPdfButton from '@/components/ui/download-pdf-button'

export const fragment = graphql`
  fragment LandingPage on ContentfulLandingPage {
    id
    slug
    __typename
    seo {
      ...SEO
    }
  }

  fragment ExperienceContent on ContentfulNinetailedExperience {
    id: contentful_id
    audience: nt_audience {
      id: contentful_id
    }
    name: nt_name
    type: nt_type
    config: nt_config {
      components {
        baseline {
          id
          title
        }
        variants {
          hidden
          id
        }
      }
      traffic
      distribution
    }
  }
`

export const query = graphql`
  query ContentfulLandingPage($id: String!) {
    contentfulLandingPage(id: { eq: $id }) {
      id
      __typename
      seo {
        ...SEO
      }
      backgroundColor
      downloadAsPdf
      headerNav {
        ...Header
        nt_experiences {
          ...ExperienceContent
          ... on ContentfulNinetailedExperience {
            variants: nt_variants {
              ...Header
            }
          }
        }
      }
      darkMode
      pageBackgroundImage {
        ...Asset
      }
      sections {
        ... on ContentfulSectionCta {
          ...SectionCta
        }
        ... on ContentfulSectionFaqs {
          ...SectionFaqs
        }
        ... on ContentfulSectionForm {
          ...SectionForm
        }
        ... on ContentfulSectionPrebuilt {
          ...SectionPrebuilt
          nt_experiences {
            ...ExperienceContent
            ... on ContentfulNinetailedExperience {
              variants: nt_variants {
                ...SectionPrebuilt
              }
            }
          }
        }
        ... on ContentfulComponentImage {
          ...ComponentImage
        }
        ... on ContentfulComponentQuote {
          ...Quote
        }
        ... on ContentfulSectionSetOfCards {
          ...SectionSetOfCards
          nt_experiences {
            ...ExperienceContent
            ... on ContentfulNinetailedExperience {
              variants: nt_variants {
                ...SectionSetOfCards
              }
            }
          }
        }
        ... on ContentfulSectionSetOfImages {
          ...SectionSetOfImages
          nt_experiences {
            ...ExperienceContent
            ... on ContentfulNinetailedExperience {
              variants: nt_variants {
                ...SectionSetOfImages
                ...SectionSetOfCards
              }
            }
          }
        }
        ... on ContentfulSectionSetOfPricing {
          ...SectionSetOfPricing
          nt_experiences {
            ...ExperienceContent
            ... on ContentfulNinetailedExperience {
              variants: nt_variants {
                ...SectionSetOfPricing
              }
            }
          }
        }
        ... on ContentfulSectionTabSection {
          ...SectionTabSection
        }
        ... on ContentfulSectionMasonary {
          ...SectionMasonary
        }
        ... on ContentfulComponentRichText {
          ...RichText
        }
        ... on ContentfulLandingRichText {
          __typename
          contentful_id
          contentWidth
          tableColor
          textAlign
          text {
            ...RichText
          }
        }
        ... on ContentfulSectionSlider {
          ...SectionSlider
        }
        ... on ContentfulSectionBarGraph {
          ...SectionBarGraph
        }
        ... on ContentfulComponentLottie {
          ...ComponentLottie
        }
        ... on ContentfulComponentComparisonTable {
          ...ComparisonTable
        }
        ... on ContentfulSectionEventSchedule {
          ...EventSchedule
        }
      }
    }
  }
`
// declare dataLayer
declare global {
  interface Window {
    dataLayer: any[]
  }
}

export default function LandingPage(props: PageProps<Queries.ContentfulLandingPageQuery>) {
  const {
    backgroundColor = 'White',
    headerNav,
    seo,
    sections,
    darkMode,
    pageBackgroundImage,
    downloadAsPdf,
  } = props.data.contentfulLandingPage as {
    seo: any
    sections: any
    backgroundColor?: string
    headerNav: Queries.HeaderFragment
    darkMode?: boolean
    pageBackgroundImage?: Queries.AssetFragment
    downloadAsPdf?: boolean
  }
  const isPrintView = isPrint()
  const { origin, pathname } = useLocation()

  const { indigoBlue, primaryBlue, lightGray, white } = colors
  const { identify } = useNinetailed()

  useEffect(() => {
    // send user browser and os to Ninetailed
    identify('', { browser: getBrowserName(), os: getOs() })

    // Get Clearbit data from dataLayer
    const clearbitData = window.dataLayer.find((item) => item['1'] === 'user_properties')
    // if we have clearbit data send it to Ninetailed
    if (clearbitData && clearbitData[2]) {
      identify('', clearbitData[2])
    }
  }, [])

  let printTitle = seo?.title
  let printDescription = seo?.description?.description
  let printTagline = ''

  if (
    sections?.length > 0 &&
    sections[0].__typename === 'ContentfulSectionSetOfCards' &&
    sections[0].cards?.length === 1
  ) {
    printTitle = sections[0].cards[0]?.headline
    printDescription = sections[0].cards[0]?.subtext
    printTagline = sections[0].cards[0]?.tagline
  }

  const Wrapper = useCallback(
    ({ children }) =>
      isPrintView ? (
        <PrintWrapper
          coverImage={printCoverImage.fingerprint}
          pageType="landingPage"
          title={printTitle}
          description={printDescription}
          tagline={printTagline}
          pageUrl={origin + pathname}
        >
          {children}
        </PrintWrapper>
      ) : (
        <PageWrapper
          seo={seo}
          bodyTw={tw`flex-grow mb-12 [&>section]:mb-10 lg:[&>section]:mb-20 [&>section]:mt-10 relative z-1`}
          headerNav={headerNav}
          otherLocales={(props.pageContext as any).otherLocales}
          darkMode={darkMode}
        >
          {children}
          {downloadAsPdf && (
            <section tw="max-w-8xl mx-auto px-5 lg:px-9">
              <div tw="text-center px-5 py-6">
                <DownloadPdfButton
                  btnText="Get page details as PDF"
                  backgroundColor={colors.white}
                  buttonStyle={buttonStyles.btnOutlined}
                />
              </div>
            </section>
          )}
        </PageWrapper>
      ),
    [isPrintView]
  )

  return (
    <div
      tw="dark:bg-gray-700 flex flex-col min-h-screen"
      css={[
        backgroundColor === indigoBlue && tw`bg-indigoBlue`,
        backgroundColor === primaryBlue && tw`bg-primaryBlue`,
        backgroundColor === lightGray && tw`bg-gray-100`,
      ]}
    >
      <div tw="hidden lg:block absolute z-0 top-0 left-0 right-0 z-0 w-full [&_svg]:(fill-none w-full) [&_figure]:(w-full) [&_img]:(w-full)">
        <div
          tw="relative max-h-[800px] overflow-hidden after:(absolute z-1 inset-x-0 bottom-0 md:h-[20%] lg:h-[40%] bg-gradient-to-b from-transparent dark:to-gray-700!)"
          css={[
            backgroundColor === white && tw`after:to-white`,
            backgroundColor === lightGray && tw`after:to-gray-100`,
            backgroundColor === primaryBlue && tw`after:to-primaryBlue`,
            backgroundColor === indigoBlue && tw`after:to-indigoBlue`,
          ]}
        >
          <Image image={pageBackgroundImage} />
        </div>
      </div>
      <Wrapper>
        {sections?.map((section, index) => {
          const key = section.contentful_id || section.id
          const previewInspectorProps = getInspectorProps({ entryId: key, fieldId: 'sections' })
          if (section.__typename === 'ContentfulSectionSetOfCards') {
            const isHero = index === 0 && section.cards?.length === 1

            // Don't render hero section in print view (it's already rendered in the cover)
            return isHero && isPrintView ? null : (
              <SetOfCards
                key={key}
                {...section}
                isHero={isHero}
                pageColor={backgroundColor}
                pageOrderNo={index}
                {...previewInspectorProps}
                isPrintView={isPrintView}
              />
            )
          }
          if (section.__typename === 'ContentfulComponentImage')
            return (
              !isPrintView && (
                <section key={key} tw="max-w-8xl m-auto px-5 lg:px-9" {...previewInspectorProps}>
                  <Image {...section} />
                </section>
              )
            )
          if (section.__typename === 'ContentfulComponentQuote')
            return (
              <section key={key} tw="max-w-8xl m-auto px-5 lg:px-9" {...previewInspectorProps}>
                <div tw="max-w-3xl mx-auto">
                  <Quote {...section} />
                </div>
              </section>
            )
          if (section.__typename === 'ContentfulSectionCta' && !isPrintView)
            return <SectionCta key={key} {...section} {...previewInspectorProps} />

          if (section.__typename === 'ContentfulSectionForm' && !isPrintView)
            return <SectionForm key={key} {...section} {...previewInspectorProps} />

          if (section.__typename === 'ContentfulSectionFaqs')
            return (
              <SectionFaqs
                key={key}
                {...section}
                pageColor={backgroundColor}
                {...previewInspectorProps}
                isPrintView={isPrintView}
              />
            )

          if (section.__typename === 'ContentfulSectionPrebuilt' && !isPrintView)
            return <SectionPrebuilt key={key} {...section} {...previewInspectorProps} />

          if (section.__typename === 'ContentfulSectionSetOfImages' && !isPrintView)
            return (
              <SetOfImages
                key={key}
                {...section}
                pageColor={backgroundColor}
                pageOrderNo={index}
                {...previewInspectorProps}
                css={[
                  // Remove margin between sections if the next section is a SetOfImages without a heading
                  index !== sections.length - 1 &&
                    sections[index + 1].__typename === 'ContentfulSectionSetOfImages' &&
                    !sections[index + 1].heading &&
                    tw`mb-0!`,
                  index > 0 &&
                    !section.heading &&
                    sections[index - 1].__typename === 'ContentfulSectionSetOfImages' &&
                    tw`mt-0!`,
                ]}
              />
            )
          if (section.__typename === 'ContentfulSectionTabSection' && !isPrintView)
            return <SectionTabSection key={key} {...section} {...previewInspectorProps} isHero={index === 0} />

          if (section.__typename === 'ContentfulSectionSetOfPricing' && !isPrintView)
            return (
              <div id="pricing" key={key} {...previewInspectorProps}>
                <SectionSetOfPricing {...section} />
              </div>
            )

          if (section.__typename === 'ContentfulSectionMasonary' && !isPrintView)
            return <SetOfMasonaryCards key={key} {...section} {...previewInspectorProps} />

          if (section.__typename === 'ContentfulComponentRichText')
            return !isPrintView ? (
              <section
                key={key}
                tw="max-w-4xl mx-auto px-5"
                css={[(backgroundColor === indigoBlue || backgroundColor === primaryBlue) && tw`text-white`]}
                {...previewInspectorProps}
              >
                <RichText
                  {...section}
                  renderImage={(ref) => (
                    <div tw="pt-6 pb-12 flex justify-center">
                      <Image image={ref} />
                    </div>
                  )}
                />
              </section>
            ) : (
              <RichText {...section} />
            )

          if (section.__typename === 'ContentfulLandingRichText') {
            const { contentWidth, textAlign, tableColor } = section
            const richText = { body: section.text?.body, tableColor, contentWidth, textAlign }

            return !isPrintView ? (
              <section
                key={key}
                css={[
                  tableColor === 'gray' && tw`bg-white`,
                  tableColor === 'white' && tw`bg-gray-100`,
                  contentWidth !== 'wide' && tw`max-w-3xl mx-auto py-5 lg:py-20 px-5 lg:px-9`,
                ]}
                {...previewInspectorProps}
              >
                <div
                  css={[
                    tw`[.table-container]:mb-10 lg:[.table-container]:mb-20 [.table-container:last-of-type]:mb-0`,
                    contentWidth === 'wide' && tw`mx-auto max-w-8xl py-5 lg:py-20 px-5 lg:px-9`,
                    textAlign === 'center' && tw`text-center`,
                    textAlign === 'right' && tw`text-right`,
                  ]}
                >
                  <RichText
                    {...richText}
                    renderImage={(ref) => (
                      <div tw="pt-6 pb-12 flex justify-center">
                        <Image image={ref} />
                      </div>
                    )}
                  />
                </div>
              </section>
            ) : (
              <RichText {...richText} />
            )
          }

          if (section.__typename === 'ContentfulSectionSlider' && !isPrintView) {
            return (
              <section key={key} {...previewInspectorProps}>
                <SliderSection {...section} />
              </section>
            )
          }
          if (section.__typename === 'ContentfulSectionBarGraph' && !isPrintView) {
            return (
              <section key={key} tw="max-w-8xl mx-auto px-5 lg:px-9" {...previewInspectorProps}>
                <BarGraph {...section} />
              </section>
            )
          }
          if (section.__typename === 'ContentfulComponentLottie' && !isPrintView) {
            return (
              <section key={key} tw="max-w-8xl mx-auto px-5 lg:px-9" {...previewInspectorProps}>
                <Lottie {...section} />
              </section>
            )
          }

          if (section.__typename === 'ContentfulComponentComparisonTable') {
            return <ComparisonTable key={key} {...section} {...previewInspectorProps} isPrintView={isPrintView} />
          }

          if (section.__typename === 'ContentfulSectionEventSchedule' && !isPrintView) {
            return (
              <section key={key} {...previewInspectorProps}>
                <EventSchedule {...section} backgroundColor={backgroundColor} />
              </section>
            )
          }

          if (section.__typename === 'ContentfulComponentTable') {
            return <Table key={key} {...section} {...previewInspectorProps} />
          }

          return null
        })}
      </Wrapper>
    </div>
  )
}
